import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { QrCode, Settings, FlashlightOff, Flashlight, LogIn, User, Camera, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// App version
const APP_VERSION = "1.5.0";

const ScannerPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);
  const [showPermissionRequest, setShowPermissionRequest] = useState(false);
  const html5QrCodeRef = useRef(null);
  const isRunningRef = useRef(false);
  const mountedRef = useRef(true);
  const streamRef = useRef(null);

  const stopScanner = useCallback(async () => {
    // Stop html5-qrcode scanner
    if (html5QrCodeRef.current && isRunningRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.log("Scanner stop:", err.message);
      }
      isRunningRef.current = false;
    }
    // Release media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleScanSuccess = useCallback(async (decodedText) => {
    await stopScanner();
    
    if (!mountedRef.current) return;
    
    let code = decodedText;
    
    if (decodedText.includes('/q/')) {
      const match = decodedText.match(/\/q\/([A-Za-z0-9]+)/);
      if (match) {
        code = match[1];
      }
    }
    
    toast.success("QR Code detected!");
    navigate(`/q/${code.toUpperCase()}`);
  }, [navigate, stopScanner]);

  // Step 1: Request camera permission explicitly
  const requestCameraPermission = async () => {
    setShowPermissionRequest(false);
    setIsInitializing(true);
    
    try {
      console.log("Requesting camera permission...");
      
      // First, try to get user media to trigger permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log("Camera permission granted, got stream");
      streamRef.current = stream;
      
      // Permission granted, now start the scanner
      await startQRScanner();
      
    } catch (err) {
      console.error("Camera permission error:", err);
      setIsInitializing(false);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        setErrorMessage("Camera permission was denied. Please go to your browser settings and allow camera access for this site, then reload the page.");
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setPermissionDenied(true);
        setErrorMessage("No camera detected on your device. Please use a device with a camera or enter the code manually.");
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setPermissionDenied(true);
        setErrorMessage("Camera is being used by another app. Please close all other apps that might be using the camera and try again.");
      } else if (err.name === 'OverconstrainedError') {
        // Try again with basic constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ video: true });
          streamRef.current = basicStream;
          await startQRScanner();
        } catch (e) {
          setPermissionDenied(true);
          setErrorMessage("Could not access camera. Please check your browser settings.");
        }
      } else if (err.name === 'SecurityError') {
        setPermissionDenied(true);
        setErrorMessage("Camera access requires a secure connection (HTTPS). Please make sure you're using https://");
      } else {
        setPermissionDenied(true);
        setErrorMessage(`Camera error: ${err.message || 'Unknown error'}. Please try a different browser.`);
      }
    }
  };

  // Step 2: Start QR Scanner after permission is granted
  const startQRScanner = async () => {
    const element = document.getElementById("qr-reader");
    if (!element || !mountedRef.current) {
      console.error("QR reader element not found");
      setIsInitializing(false);
      return;
    }

    // Stop any existing stream before starting scanner
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    try {
      const html5QrCode = new Html5Qrcode("qr-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        disableFlip: false,
      };

      // Try environment (back) camera first
      try {
        console.log("Starting scanner with environment camera...");
        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          handleScanSuccess,
          () => {}
        );
        isRunningRef.current = true;
        setScannerReady(true);
        setIsInitializing(false);
        console.log("Scanner started successfully");
        
        // Check for flash
        try {
          const capabilities = html5QrCode.getRunningTrackCapabilities();
          if (capabilities?.torch) {
            setHasFlash(true);
          }
        } catch (e) {}
        
        return;
      } catch (envErr) {
        console.warn("Environment camera failed:", envErr.message);
      }

      // Fallback to user (front) camera
      try {
        console.log("Trying user camera...");
        await html5QrCode.start(
          { facingMode: "user" },
          config,
          handleScanSuccess,
          () => {}
        );
        isRunningRef.current = true;
        setScannerReady(true);
        setIsInitializing(false);
        console.log("Scanner started with user camera");
        return;
      } catch (userErr) {
        console.warn("User camera failed:", userErr.message);
      }

      // If both fail, show error
      throw new Error("Could not start camera scanner");

    } catch (err) {
      console.error("QR Scanner error:", err);
      setIsInitializing(false);
      setScannerReady(true); // Still show UI for manual entry
      toast.error("Scanner failed - use manual entry");
    }
  };

  // Check if camera API is available
  useEffect(() => {
    mountedRef.current = true;

    const checkCameraSupport = async () => {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setIsInitializing(false);
        setPermissionDenied(true);
        setErrorMessage("Your browser doesn't support camera access. Please use a modern browser like Chrome, Safari, or Firefox.");
        return;
      }

      // Check current permission state if available
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'camera' });
          console.log("Camera permission state:", result.state);
          
          if (result.state === 'granted') {
            // Permission already granted, start scanner directly
            await requestCameraPermission();
          } else if (result.state === 'denied') {
            // Permission was denied
            setIsInitializing(false);
            setPermissionDenied(true);
            setErrorMessage("Camera permission was previously denied. Please go to your browser settings, allow camera access for this site, then reload the page.");
          } else {
            // Permission is 'prompt' - show button to request
            setIsInitializing(false);
            setShowPermissionRequest(true);
          }
          
          // Listen for permission changes
          result.onchange = () => {
            console.log("Permission changed to:", result.state);
            if (result.state === 'granted' && mountedRef.current) {
              requestCameraPermission();
            }
          };
        } else {
          // Permissions API not available, show request button
          setIsInitializing(false);
          setShowPermissionRequest(true);
        }
      } catch (e) {
        // Permissions query failed, just show request button
        console.log("Permissions query not supported:", e);
        setIsInitializing(false);
        setShowPermissionRequest(true);
      }
    };

    checkCameraSupport();

    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, []);

  const toggleFlash = async () => {
    if (html5QrCodeRef.current && isRunningRef.current) {
      try {
        await html5QrCodeRef.current.applyVideoConstraints({
          advanced: [{ torch: !flashOn }]
        });
        setFlashOn(!flashOn);
      } catch (err) {
        toast.error("Flash not available on this device");
      }
    }
  };

  const handleManualEntry = () => {
    const code = prompt("Enter the 12-character QR Code:\n(e.g., ABC123DEF456)");
    if (code && code.trim()) {
      const cleanCode = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (cleanCode.length >= 6) {
        navigate(`/q/${cleanCode}`);
      } else {
        toast.error("Please enter a valid code (at least 6 characters)");
      }
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  // Show permission request button
  if (showPermissionRequest) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-[#2563EB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="w-10 h-10 text-[#2563EB]" />
          </div>
          <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope' }}>
            Camera Access Needed
          </h2>
          <p className="text-[#475569] mb-6 text-sm">
            To scan QR codes, we need access to your camera. Tap the button below and allow camera access when prompted.
          </p>
          <div className="space-y-3">
            <Button
              onClick={requestCameraPermission}
              className="w-full h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-lg"
              data-testid="enable-camera-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              Enable Camera
            </Button>
            <Button
              onClick={handleManualEntry}
              variant="outline"
              className="w-full h-12 border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]"
              data-testid="manual-entry-btn"
            >
              <QrCode className="w-5 h-5 mr-2" />
              Enter Code Manually
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full h-10 text-[#475569] hover:text-[#0F172A]"
            >
              Back to Home
            </Button>
          </div>
          <p className="text-[#94A3B8] text-xs mt-6">v{APP_VERSION}</p>
        </div>
      </div>
    );
  }

  // Permission denied / Error state
  if (permissionDenied) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope' }}>
            Camera Issue
          </h2>
          <p className="text-[#475569] mb-6 text-sm">
            {errorMessage}
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleManualEntry}
              className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8]"
              data-testid="manual-entry-btn"
            >
              <QrCode className="w-5 h-5 mr-2" />
              Enter Code Manually
            </Button>
            <Button
              onClick={handleRetry}
              variant="outline"
              className="w-full h-12 border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]"
              data-testid="retry-btn"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full h-10 text-[#475569] hover:text-[#0F172A]"
            >
              Back to Home
            </Button>
          </div>
          
          {/* Troubleshooting tips */}
          <div className="mt-6 p-4 bg-[#F1F5F9] rounded-lg text-left border border-[#E2E8F0]">
            <p className="text-[#475569] text-xs font-medium mb-2">Troubleshooting:</p>
            <ul className="text-[#64748B] text-xs space-y-1">
              <li>• Check browser settings → Site Settings → Camera</li>
              <li>• Make sure no other app is using the camera</li>
              <li>• Try using Chrome browser</li>
              <li>• Restart your browser and try again</li>
            </ul>
          </div>
          
          <p className="text-[#94A3B8] text-xs mt-4">v{APP_VERSION}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scanner-overlay">
      {/* Scanner element */}
      <div 
        id="qr-reader" 
        className="w-full h-full absolute" 
        style={{ opacity: scannerReady ? 1 : 0 }} 
      />
      
      {/* Loading overlay */}
      {isInitializing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Starting camera...</p>
            <p className="text-gray-400 text-sm mt-2">Please wait...</p>
          </div>
        </div>
      )}
      
      {/* Scanner frame */}
      <div className="scanner-frame">
        <div className="scanner-corner scanner-corner-tl" />
        <div className="scanner-corner scanner-corner-tr" />
        <div className="scanner-corner scanner-corner-bl" />
        <div className="scanner-corner scanner-corner-br" />
        <div className="scan-line" />
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-10">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <QrCode className="w-6 h-6 text-[#2563EB]" />
          </div>
          <span className="text-white font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>
            QuickQRID
          </span>
        </div>
        {hasFlash && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={toggleFlash}
            data-testid="flash-toggle-btn"
          >
            {flashOn ? <Flashlight className="w-6 h-6" /> : <FlashlightOff className="w-6 h-6" />}
          </Button>
        )}
      </div>

      {/* Instructions */}
      <div className="absolute top-[28%] left-0 right-0 text-center text-white z-10">
        <p className="text-lg font-medium" style={{ fontFamily: 'Manrope' }}>
          Point camera at QR code
        </p>
        <p className="text-sm text-white/70 mt-2">
          Scanning will start automatically
        </p>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent z-10">
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleManualEntry}
            variant="outline"
            className="w-full h-12 bg-white/10 border-white/30 text-white hover:bg-white/20"
            data-testid="manual-entry-btn"
          >
            <QrCode className="w-5 h-5 mr-2" />
            Enter Code Manually
          </Button>
          
          <div className="flex gap-3">
            {user ? (
              <>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 h-12 bg-[#2563EB] hover:bg-[#1D4ED8]"
                  data-testid="dashboard-btn"
                >
                  <User className="w-5 h-5 mr-2" />
                  My QR Codes
                </Button>
                {isAdmin && (
                  <Button
                    onClick={() => navigate('/admin')}
                    className="h-12 px-4 bg-[#2563EB] hover:bg-[#1D4ED8]"
                    data-testid="admin-btn"
                  >
                    <Settings className="w-5 h-5" />
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  className="flex-1 h-12 bg-[#2563EB] hover:bg-[#1D4ED8]"
                  data-testid="login-btn"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Login / Sign Up
                </Button>
                <Button
                  onClick={() => navigate('/admin-login')}
                  className="h-12 px-4 bg-[#1F2937] hover:bg-[#374151] border border-white/20"
                  data-testid="admin-login-btn"
                >
                  <Settings className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
          
          <p className="text-center text-gray-500 text-xs">v{APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
